import React, { useEffect, useRef, useState } from 'react';
import { TodoList } from '@dreamai/types';
import { ReactComponent as PlusAddIcon } from '../../../icons/plus-add.svg';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import BottomSheet from '../../common/BottomSheet';
import MemberAvatar from './MemberAvatar';
import './project-lists.scss';

export const PERSONAL_LIST_ID = 'personal';

interface ProjectListsBarProps {
  lists: TodoList[];
  invitations: TodoList[];
  selectedListId: string;
  onSelect: (listId: string) => void;
  /** Resolves false when the list could not be created. */
  onCreate: (name: string) => Promise<boolean>;
  onRespond: (listId: string, response: 'accept' | 'decline') => Promise<void>;
  personalPendingCount: number;
}

/**
 * Horizontal strip of todo lists (the personal Inbox + every accepted
 * shared list), a "new list" inline form, and pending invitations with
 * explicit accept / decline actions. On mobile the tabs scroll horizontally
 * in a single row behind a leading "+" that opens the new-list bottom sheet
 * (Figma 5227:17435).
 */
const ProjectListsBar: React.FC<ProjectListsBarProps> = ({
  lists,
  invitations,
  selectedListId,
  onSelect,
  onCreate,
  onRespond,
  personalPendingCount,
}) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [responding, setResponding] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { isMobile } = useMobileNav();

  const cancelNew = () => {
    setCreating(false);
    setNewName('');
    setCreateError(null);
  };

  // Keep the active tab visible when the row scrolls horizontally (mobile).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    const active = scroller.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );
    active?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [selectedListId, lists.length]);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || saving) return;
    setSaving(true);
    setCreateError(null);
    try {
      const ok = await onCreate(name);
      if (!ok) {
        setCreateError('Could not create the list. Please try again.');
        return;
      }
      setNewName('');
      setCreating(false);
    } finally {
      setSaving(false);
    }
  };

  const respond = async (listId: string, response: 'accept' | 'decline') => {
    setResponding(listId);
    try {
      await onRespond(listId, response);
    } finally {
      setResponding(null);
    }
  };

  return (
    <div className='project-lists'>
      {invitations.length > 0 && (
        <div className='project-invitations'>
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className='project-invitation'
              data-testid={`invitation-${inv.id}`}
            >
              <MemberAvatar name={inv.ownerName} size={isMobile ? 36 : 32} />
              <div className='project-invitation-text'>
                {isMobile ? (
                  <>
                    <span className='project-invitation-title'>
                      {inv.ownerName} has invited you to {inv.name}
                    </span>
                    <span className='project-invitation-sub'>
                      Accept to see all tasks and be assigned work
                    </span>
                  </>
                ) : (
                  <>
                    <span className='project-invitation-title'>
                      <strong>{inv.ownerName}</strong> invited you to the{' '}
                      <strong>{inv.name}</strong> todo list
                    </span>
                    <span className='project-invitation-sub'>
                      Accept to see its tasks and be assigned work.
                    </span>
                  </>
                )}
              </div>
              <div className='project-invitation-actions'>
                <button
                  className='project-invitation-btn accept'
                  disabled={responding === inv.id}
                  onClick={() => respond(inv.id, 'accept')}
                >
                  Accept
                </button>
                <button
                  className='project-invitation-btn decline'
                  disabled={responding === inv.id}
                  onClick={() => respond(inv.id, 'decline')}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className='project-tabs'>
        {isMobile && (
          <button
            className='project-tab project-tab-new project-tab-new--leading'
            onClick={() => setCreating(true)}
            aria-label='New todo list'
            title='New todo list'
          >
            <PlusAddIcon />
          </button>
        )}
        <div
          ref={scrollerRef}
          className='project-tabs-scroller'
          role='tablist'
          aria-label='Todo lists'
        >
          <button
            role='tab'
            aria-selected={selectedListId === PERSONAL_LIST_ID}
            className={`project-tab${selectedListId === PERSONAL_LIST_ID ? ' active' : ''}`}
            onClick={() => onSelect(PERSONAL_LIST_ID)}
          >
            <span className='project-tab-name'>Inbox</span>
            {personalPendingCount > 0 && (
              <span className='project-tab-count'>{personalPendingCount}</span>
            )}
          </button>
          {lists.map((list) => {
            const accepted = list.members.filter(
              (m) => m.status === 'accepted',
            );
            return (
              <button
                key={list.id}
                role='tab'
                aria-selected={selectedListId === list.id}
                className={`project-tab${selectedListId === list.id ? ' active' : ''}`}
                onClick={() => onSelect(list.id)}
                title={`Owner: ${list.ownerName} · ${accepted.length} member${accepted.length === 1 ? '' : 's'}`}
              >
                <span className='project-tab-name'>{list.name}</span>
                {list.pendingTodoCount > 0 && (
                  <span className='project-tab-count'>
                    {list.pendingTodoCount}
                  </span>
                )}
                {accepted.length > 1 && (
                  <span className='project-tab-avatars' aria-hidden='true'>
                    {accepted.slice(0, 2).map((m) => (
                      <MemberAvatar
                        key={m.userId}
                        name={m.name}
                        size={isMobile ? 20 : 18}
                      />
                    ))}
                    {accepted.length > 2 && (
                      <span className='project-tab-more'>
                        +{accepted.length - 2}
                      </span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {isMobile ? (
          <BottomSheet
            open={creating}
            onClose={cancelNew}
            title='Create list'
            className='project-create-sheet'
          >
            <form
              className='bottom-sheet-form'
              onSubmit={(e) => {
                e.preventDefault();
                submitNew();
              }}
            >
              <label className='bottom-sheet-field'>
                <span className='bottom-sheet-label'>List name</span>
                <input
                  autoFocus
                  className='bottom-sheet-input'
                  placeholder='e.g. Launch plan'
                  value={newName}
                  maxLength={120}
                  aria-invalid={!!createError || undefined}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <button
                type='submit'
                className='bottom-sheet-primary'
                disabled={!newName.trim() || saving}
              >
                <span>{saving ? 'Creating…' : 'Create'}</span>
                <ArrowRightIcon />
              </button>
              {createError && (
                <p className='bottom-sheet-hint warning' role='alert'>
                  {createError}
                </p>
              )}
            </form>
          </BottomSheet>
        ) : creating ? (
          <form
            className='project-tab-new-form'
            onSubmit={(e) => {
              e.preventDefault();
              submitNew();
            }}
          >
            <input
              autoFocus
              className='project-tab-new-input'
              placeholder='List name'
              value={newName}
              maxLength={120}
              aria-invalid={!!createError || undefined}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') cancelNew();
              }}
            />
            <button
              type='submit'
              className='project-tab-new-save'
              disabled={!newName.trim() || saving}
            >
              Create
            </button>
            <button
              type='button'
              className='project-tab-new-cancel'
              onClick={cancelNew}
            >
              Cancel
            </button>
            {createError && (
              <span className='project-tab-new-error' role='alert'>
                {createError}
              </span>
            )}
          </form>
        ) : (
          <button
            className='project-tab project-tab-new'
            onClick={() => setCreating(true)}
            aria-label='New todo list'
            title='New todo list'
          >
            <PlusAddIcon />
            <span className='project-tab-new-label'>New list</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectListsBar;
